import type { ReactNode } from 'react';

import type { ApplicationStatus, Participant } from '../fixtures/domain.js';
import { Avatar } from '../components/Avatar.js';
import { Icon } from '../components/Icon.js';
import { isLocked, isLockUnknown } from '../data/unlockPending.js';
import { formatApplied } from '../util/format.js';
import { StatusTag } from './StatusTag.js';
import styles from './ConversationHeader.module.css';

interface ConversationHeaderProps {
  counterpart: Participant;
  jobTitle?: string;
  /** When the candidate applied; drawn beside the job title. */
  appliedAt?: string;
  actions?: ReactNode;
  /** Mobile only: back to the list. The button hides itself on desktop. */
  onBack?: () => void;
  /** A live pipeline chat's stage, tagged here where the column is too narrow for the stepper. */
  stage?: ApplicationStatus;
  /** Always the narrow form (back, stage tag), for a surface that is never wide. */
  compact?: boolean;
  /** Toggle the details side panel. The identity block (avatar + name + status)
   *  is the click target. Omitted where there is nothing to open — see below. */
  onToggleDetails?: () => void;
  /** Whether the details panel is currently open, for the toggle's pressed state. */
  detailsOpen: boolean;
  /** The candidate's details have not answered (loading or failed): blur an unknown lock. */
  profilePending?: boolean;
  /** The counterpart is on email, so replies come slower than in chat. */
  byEmail?: boolean;
}

export function ConversationHeader({
  counterpart,
  jobTitle,
  appliedAt,
  actions,
  onBack,
  stage,
  compact,
  onToggleDetails,
  detailsOpen,
  profilePending,
  byEmail,
}: ConversationHeaderProps) {
  const identity = (
    <>
      <Avatar
        name={counterpart.name}
        src={counterpart.avatarUrl}
        online={counterpart.online}
        locked={isLocked(counterpart)}
        blurred={profilePending && isLockUnknown(counterpart)}
        size={44}
      />
      <div className={styles.meta}>
        {/* The person is masked in a replay; the job title beside it is not —
            which screen an employer is on has to stay legible (AI-3016). */}
        <span data-ph-mask className={styles.name}>
          {counterpart.name}
        </span>
        {jobTitle && (
          <span className={styles.job}>
            <Icon name="briefcase" size={13} />
            <span className={styles.jobTitle}>{jobTitle}</span>
            {appliedAt && <span className={styles.applied}>· {formatApplied(appliedAt)}</span>}
          </span>
        )}
        {byEmail && <span className={styles.delivery}>Replies arrive by email</span>}
        {stage && (
          <span className={styles.stage}>
            <StatusTag status={stage} />
          </span>
        )}
      </div>
    </>
  );

  return (
    <header className={compact ? `${styles.header} ${styles.compact}` : styles.header}>
      {onBack && (
        <button
          className={styles.back}
          onClick={onBack}
          title="Back"
          aria-label="Back to conversations"
        >
          <Icon name="back" size={22} />
        </button>
      )}
      {/* No toggle where there is no panel: an Info thread is a platform
          announcement, and there is no person behind it to hold details about.

          A plain div rather than a disabled button on purpose. Disabled is a
          state — it says "not now" — and it would leave a greyed name at the top
          of every announcement. This is not a state. There is nothing there. */}
      {onToggleDetails ? (
        <button
          type="button"
          className={styles.identity}
          onClick={onToggleDetails}
          aria-haspopup="dialog"
          aria-expanded={detailsOpen}
          title="View details"
        >
          {identity}
        </button>
      ) : (
        <div className={styles.identityStatic}>{identity}</div>
      )}
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
